// The self-serve sign-up wizard (routes/signup.py) and what the login page
// may offer (routes/auth_google.py). Public: the wizard is behind its own
// cookie (__Host-lattice_signup; lattice_signup on a dev server), never a
// session, so these are
// plain calls, not TanStack queries (the page renders before the app's
// QueryClient exists, like /set-password).

import { apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

type S = components["schemas"];

export type AuthOptions = S["AuthOptions"];
export type SignupMe = S["SignupMe"];
export type SignupStep = SignupMe["step"];
export type SignupStage = S["SignupStage"];
export type SignupDetails = S["SignupDetails"];
export type SignupDetailsUpdate = S["SignupDetailsUpdate"];
export type SignupOptions = S["SignupOptions"];
export type SignupRole = S["SignupRole"];
export type SessionInfo = S["SessionInfo"];

/** The login page's two offers; both false when the call fails. */
export async function fetchAuthOptions(signal?: AbortSignal): Promise<AuthOptions> {
  try {
    return await apiFetch<AuthOptions>("/api/auth/options", { signal });
  } catch {
    return { google: false, signup: false };
  }
}

/** The start of a Google sign-in or sign-up: a plain link (a 303 to Google). */
export function googleStartUrl(intent: "signin" | "signup", opts: { hd?: string | null; hint?: string | null } = {}): string {
  const q = new URLSearchParams({ intent });
  if (opts.hd) q.set("hd", opts.hd);
  if (opts.hint) q.set("hint", opts.hint);
  return `/api/auth/google/start?${q.toString()}`;
}

export const signupApi = {
  me: (signal?: AbortSignal) => apiFetch<SignupMe>("/api/signup/me", { signal }),
  options: (signal?: AbortSignal) => apiFetch<SignupOptions>("/api/signup/options", { signal }),
  workCheck: (body: { work?: string; none?: boolean; confirm?: boolean }) =>
    apiFetch<SignupMe>("/api/signup/work-check", { method: "POST", body }),
  saveDetails: (body: SignupDetailsUpdate) => apiFetch<SignupMe>("/api/signup/details", { method: "PUT", body }),
  submit: () => apiFetch<SignupMe>("/api/signup/submit", { method: "POST" }),
  finish: () => apiFetch<SessionInfo>("/api/signup/finish", { method: "POST" }),
};
